// src/utils/generateShareCard.js
// Canvas API share card generation (Apple Music style — compact card)

import QRCode from "qrcode";
import { SHARED_CAT_COLOR, BEST_CATEGORIES } from "../constants";

const CARD_W = 1080;
const EDGE   = 80;
const PAD    = 56;
const INNER  = CARD_W - PAD * 2;
const R      = 36;
const PR     = 22;
const PH     = 680;

/* ── helpers ── */

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

function wrapLines(ctx, text, maxW, maxLines = 99) {
  const chars = text.split("");
  const lines = [];
  let cur = "";
  for (const ch of chars) {
    const test = cur + ch;
    if (ctx.measureText(test).width > maxW && cur) {
      if (lines.length + 1 >= maxLines) {
        lines.push(cur.trimEnd() + "…");
        return lines;
      }
      lines.push(cur);
      cur = ch;
    } else {
      cur = test;
    }
  }
  if (cur) lines.push(cur);
  return lines;
}

async function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    // External images: cache-busting to prevent CORS cache conflicts (mobile)
    if (url && url.startsWith("http")) {
      const sep = url.includes("?") ? "&" : "?";
      img.src = url + sep + "_cb=" + Date.now();
    } else {
      img.src = url;
    }
  });
}

function drawLogo(ctx, x, y, s = 1) {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(s, s);
  roundRect(ctx, 20, 14, 28, 34, 2);
  ctx.fillStyle = "#a89a8e";
  ctx.fill();
  roundRect(ctx, 14, 18, 28, 34, 2);
  ctx.fillStyle = "#a89a8e";
  ctx.fill();
  ctx.save();
  roundRect(ctx, 20, 14, 28, 34, 2);
  ctx.clip();
  roundRect(ctx, 14, 18, 28, 34, 2);
  ctx.fillStyle = "#867a6e";
  ctx.fill();
  ctx.restore();
  ctx.restore();
}

/* ── Instagram-style card ── */

const STATUS_LABEL_CARD = {
  want_to_go: "Want to go",
  visited: "Visited",
  want_revisit: "Again",
};
const STATUS_EMOJI_CARD = {
  want_to_go: "🔖",
  visited: "✅",
  want_revisit: "❤️",
};

function formatTimeCard(dateStr) {
  if (!dateStr) return "";
  const diff = Math.floor((new Date() - new Date(dateStr)) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function drawAvatarCircle(ctx, x, y, size, nickname, fh) {
  const grad = ctx.createLinearGradient(x, y, x + size, y + size);
  grad.addColorStop(0, "#595149");
  grad.addColorStop(1, "#655d54");
  ctx.beginPath();
  ctx.arc(x + size / 2, y + size / 2, size / 2, 0, Math.PI * 2);
  ctx.fillStyle = grad;
  ctx.fill();
  ctx.save();
  ctx.font = `italic 700 ${Math.round(size * 0.45)}px ${fh}`;
  ctx.fillStyle = "#fff6ef";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText((nickname || "?")[0].toUpperCase(), x + size / 2, y + size / 2);
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.restore();
}

export default async function generateShareCard(place, user) {
  await document.fonts.ready;

  const FH = "'Noto Serif', Georgia, serif";
  const FL = "'Manrope', -apple-system, sans-serif";

  const cat = BEST_CATEGORIES.find((c) => c.key === place.category);
  const nickname = user?.nickname || place.owner_nickname || "User";
  const photoUrls = place.photo_urls?.length ? place.photo_urls : (place.photo_url ? [place.photo_url] : []);

  /* layout constants — 1.3x font scale */
  const HEADER_H = 110;

  /* pre-load photo to determine aspect ratio */
  let preloadedImg = null;
  if (photoUrls.length) {
    try { preloadedImg = await loadImage(photoUrls[0]); } catch { /* ignore */ }
  }
  // photo: full card width, original aspect ratio preserved
  const PHOTO_W = CARD_W;
  const PHOTO_H = preloadedImg
    ? Math.round(PHOTO_W * (preloadedImg.height / preloadedImg.width))
    : Math.round(PHOTO_W * 0.6);

  /* measure bottom section (1.3x fonts) */
  const mc = document.createElement("canvas").getContext("2d");
  mc.font = `700 52px ${FL}`;
  const nameLines = wrapLines(mc, place.name, INNER, 2);
  const nameLH = 66;

  let bottomH = 30;
  bottomH += nameLines.length * nameLH;
  if (place.status || place.rating > 0) bottomH += 14 + 48;
  if (place.memo) bottomH += 14 + 44;
  if (place.address) bottomH += 14 + 38;
  bottomH += 30;

  /* card height */
  const ch = HEADER_H + PHOTO_H + bottomH;

  /* canvas */
  const W = CARD_W + EDGE * 2;
  const H = ch + EDGE * 2;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, W, H);

  /* card shadow + bg */
  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,0.08)";
  ctx.shadowBlur = 32;
  ctx.shadowOffsetY = 6;
  roundRect(ctx, EDGE, EDGE, CARD_W, ch, R);
  ctx.fillStyle = "#ffffff";
  ctx.fill();
  ctx.restore();

  /* clip to card */
  ctx.save();
  roundRect(ctx, EDGE, EDGE, CARD_W, ch, R);
  ctx.clip();
  ctx.textBaseline = "top";

  const L = EDGE + PAD;
  let y = EDGE;

  /* ── HEADER: avatar + nickname + badge + category + time ── */
  const avatarS = 72;
  const hY = y + (HEADER_H - avatarS) / 2;

  if (user?.profile_photo_url) {
    try {
      const avImg = await loadImage(user.profile_photo_url);
      ctx.save();
      ctx.beginPath();
      ctx.arc(L + avatarS / 2, hY + avatarS / 2, avatarS / 2, 0, Math.PI * 2);
      ctx.clip();
      ctx.drawImage(avImg, L, hY, avatarS, avatarS);
      ctx.restore();
    } catch {
      drawAvatarCircle(ctx, L, hY, avatarS, nickname, FH);
    }
  } else {
    drawAvatarCircle(ctx, L, hY, avatarS, nickname, FH);
  }

  let hx = L + avatarS + 18;

  // nickname
  ctx.font = `700 39px ${FL}`;
  ctx.fillStyle = "#1c1c1e";
  ctx.fillText(nickname, hx, hY + 6);
  hx += ctx.measureText(nickname).width + 12;

  // "Me" badge
  roundRect(ctx, hx, hY + 12, 52, 28, 6);
  ctx.fillStyle = "#e8e8ed";
  ctx.fill();
  ctx.font = `700 18px ${FL}`;
  ctx.fillStyle = "#8e8e93";
  ctx.textAlign = "center";
  ctx.fillText("Me", hx + 26, hY + 16);
  ctx.textAlign = "left";

  // category (second line)
  if (cat) {
    ctx.font = `400 28px ${FL}`;
    ctx.fillStyle = "#8e8e93";
    ctx.fillText(cat.label, L + avatarS + 18, hY + 46);
  }

  y += HEADER_H;

  /* ── PHOTO (full width, original aspect ratio) ── */
  let hasPhoto = false;
  if (preloadedImg) {
    ctx.save();
    ctx.beginPath();
    ctx.rect(EDGE, y, CARD_W, PHOTO_H);
    ctx.clip();
    ctx.drawImage(preloadedImg, EDGE, y, PHOTO_W, PHOTO_H);
    ctx.restore();
    hasPhoto = true;
  }
  if (!hasPhoto) {
    const cc = SHARED_CAT_COLOR[place.category] || "#655d54";
    ctx.save();
    ctx.beginPath();
    ctx.rect(EDGE, y, CARD_W, PHOTO_H);
    ctx.clip();
    ctx.fillStyle = cc + "12";
    ctx.fillRect(EDGE, y, CARD_W, PHOTO_H);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = "120px serif";
    ctx.fillStyle = cc + "40";
    ctx.fillText(cat?.emoji || "📍", EDGE + CARD_W / 2, y + PHOTO_H / 2);
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.restore();
  }

  y += PHOTO_H;

  /* ── BOTTOM: name, status, memo, address (1.3x fonts) ── */
  let bx = L;
  let by = y + 30;

  // place name — 40→52
  ctx.font = `700 52px ${FL}`;
  ctx.fillStyle = "#1c1c1e";
  for (const line of nameLines) {
    ctx.fillText(line, bx, by);
    by += nameLH;
  }

  // status badge + rating — 22→29
  if (place.status || place.rating > 0) {
    by += 14;
    let sx = bx;

    if (place.status) {
      const emoji = STATUS_EMOJI_CARD[place.status] || "";
      const label = STATUS_LABEL_CARD[place.status] || place.status;
      const badgeText = emoji + " " + label;
      ctx.font = `700 29px ${FL}`;
      const tw = ctx.measureText(badgeText).width + 34;
      roundRect(ctx, sx, by, tw, 44, 10);
      const statusColors = {
        want_to_go: { bg: "#FEF3CD", color: "#BA7517" },
        visited: { bg: "#E0F4EC", color: "#1D9E75" },
        want_revisit: { bg: "#FCE4EE", color: "#D4537E" },
      };
      const sc = statusColors[place.status] || { bg: "#f0f0f0", color: "#666" };
      ctx.fillStyle = sc.bg;
      ctx.fill();
      ctx.font = `700 29px ${FL}`;
      ctx.fillStyle = sc.color;
      ctx.fillText(badgeText, sx + 17, by + 8);
      sx += tw + 16;
    }

    if (place.rating > 0) {
      const stars = "★".repeat(place.rating) + "☆".repeat(5 - place.rating);
      ctx.font = `400 29px ${FL}`;
      const starsW = ctx.measureText(stars).width + 28;
      roundRect(ctx, sx, by, starsW, 44, 10);
      ctx.fillStyle = "#f0f0f0";
      ctx.fill();
      ctx.font = `400 29px ${FL}`;
      ctx.fillStyle = "#1c1c1e";
      ctx.fillText(stars, sx + 14, by + 8);
    }

    by += 48;
  }

  // memo — 28→36
  if (place.memo) {
    by += 14;
    ctx.font = `italic 400 36px ${FH}`;
    ctx.fillStyle = "#3c3c3e";
    const memoText = `"${place.memo}"`;
    const memoLines = wrapLines(ctx, memoText, INNER, 2);
    ctx.fillText(memoLines.join(" "), bx, by);
    by += 44;
  }

  // address — 22→29
  if (place.address) {
    by += 14;
    ctx.font = `400 29px ${FL}`;
    ctx.fillStyle = "#8e8e93";
    ctx.fillText("📍 " + place.address, bx, by);
    by += 38;
  }

  ctx.restore();

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/png");
  });
}

/* ══════════════════════════════════════════════════════════
   Profile share card (Apple Music style)
   ══════════════════════════════════════════════════════════ */

const STATUS_DOT = { want_to_go: "#BA7517", visited: "#1D9E75", want_revisit: "#D4537E" };
const MAP_H = Math.round(INNER * 0.95);  // ~4:5 portrait ratio for OG preview

async function drawMapArea(ctx, places, x, y, w, h, r) {
  ctx.save();
  roundRect(ctx, x, y, w, h, r);
  ctx.clip();

  // Fallback background
  ctx.fillStyle = "#e8efe4";
  ctx.fillRect(x, y, w, h);

  if (places.length === 0) {
    ctx.font = `italic 400 16px 'Noto Serif', Georgia, serif`;
    ctx.fillStyle = "#a0a8a0";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("No saved places yet", x + w / 2, y + h / 2);
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.restore();
    return;
  }

  // Calculate bounds for projection
  let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
  places.forEach((p) => {
    if (p.lng < minLng) minLng = p.lng;
    if (p.lng > maxLng) maxLng = p.lng;
    if (p.lat < minLat) minLat = p.lat;
    if (p.lat > maxLat) maxLat = p.lat;
  });
  const lngPad = Math.max((maxLng - minLng) * 0.15, 0.01);
  const latPad = Math.max((maxLat - minLat) * 0.15, 0.01);
  minLng -= lngPad; maxLng += lngPad;
  minLat -= latPad; maxLat += latPad;

  // Try loading Google Maps Static image as background
  const mapsKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;
  if (mapsKey) {
    try {
      const cLat = ((minLat + maxLat) / 2).toFixed(6);
      const cLng = ((minLng + maxLng) / 2).toFixed(6);
      // Estimate zoom from bounds (rough)
      const latSpan = maxLat - minLat;
      const zoom = Math.max(2, Math.min(15, Math.round(Math.log2(360 / latSpan) - 1)));
      const staticUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${cLat},${cLng}&zoom=${zoom}&size=680x544&scale=2&style=feature:all|element:labels|visibility:off&style=feature:road|element:geometry|color:0xf5f5f5&style=feature:water|color:0xc9d9e8&style=feature:landscape|color:0xe8efe4&style=feature:poi|visibility:off&key=${mapsKey}`;
      const mapImg = await loadImage(staticUrl);
      ctx.drawImage(mapImg, x, y, w, h);
    } catch {
      // Keep fallback background
    }
  }

  // Place dots
  const dotR = places.length > 30 ? 5 : places.length > 15 ? 6 : 7;
  places.forEach((p) => {
    const px = x + ((p.lng - minLng) / (maxLng - minLng)) * w;
    const py = y + ((maxLat - p.lat) / (maxLat - minLat)) * h;
    const color = STATUS_DOT[p.status] || "#655d54";
    // glow
    ctx.beginPath(); ctx.arc(px, py, dotR + 6, 0, Math.PI * 2);
    ctx.fillStyle = color + "15"; ctx.fill();
    // outer ring
    ctx.beginPath(); ctx.arc(px, py, dotR + 3, 0, Math.PI * 2);
    ctx.fillStyle = color + "25"; ctx.fill();
    // dot
    ctx.beginPath(); ctx.arc(px, py, dotR, 0, Math.PI * 2);
    ctx.fillStyle = color; ctx.fill();
    ctx.strokeStyle = "#fff"; ctx.lineWidth = 2; ctx.stroke();
  });

  ctx.restore();
}


export async function generateProfileCard(user, places, profileUrl) {
  await document.fonts.ready;

  const FH = "'Noto Serif', Georgia, serif";
  const FL = "'Manrope', -apple-system, sans-serif";

  const statLine = `${places.length} curated places`;
  const QR_SIZE = 100;

  /* card height */
  let ch = PAD + MAP_H + 48;    // map + gap
  ch += 130;                      // avatar row (120px avatar)
  ch += 20 + 44;                  // gap + stat line
  ch += 48 + 1 + 36 + 56 + PAD;  // divider area + brand/QR + bottom

  const W = CARD_W + EDGE * 2;
  const H = ch + EDGE * 2;
  const canvas = document.createElement("canvas");
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, W, H);

  /* shadow + card bg */
  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,0.08)";
  ctx.shadowBlur = 32; ctx.shadowOffsetY = 6;
  roundRect(ctx, EDGE, EDGE, CARD_W, ch, R);
  ctx.fillStyle = "#fff"; ctx.fill();
  ctx.restore();

  /* clip */
  ctx.save();
  roundRect(ctx, EDGE, EDGE, CARD_W, ch, R);
  ctx.clip();
  ctx.textBaseline = "top";

  const L = EDGE + PAD;
  let y = EDGE + PAD;

  /* map — place dots */
  await drawMapArea(ctx, places, L, y, INNER, MAP_H, PR);
  y += MAP_H + 48;

  /* avatar + name row */
  const avatarSize = 120;
  if (user.profile_photo_url) {
    try {
      const img = await loadImage(user.profile_photo_url);
      ctx.save();
      ctx.beginPath();
      ctx.arc(L + avatarSize / 2, y + avatarSize / 2, avatarSize / 2, 0, Math.PI * 2);
      ctx.clip();
      ctx.drawImage(img, L, y, avatarSize, avatarSize);
      ctx.restore();
    } catch {
      drawAvatarFallback(ctx, L, y, avatarSize, user.nickname, FH);
    }
  } else {
    drawAvatarFallback(ctx, L, y, avatarSize, user.nickname, FH);
  }

  const tx = L + avatarSize + 24;
  ctx.font = `700 64px ${FH}`;
  ctx.fillStyle = "#1c1c1e";
  ctx.fillText(user.nickname, tx, y + 24);
  y += 130;

  /* stat line */
  y += 20;
  ctx.font = `600 36px ${FL}`;
  ctx.fillStyle = "#5c605c";
  ctx.fillText(statLine, L, y);
  y += 44;

  /* divider */
  y += 48;
  ctx.fillStyle = "#e8e8ed";
  ctx.fillRect(L, y, INNER, 1);
  y += 1 + 36;

  /* branding + QR code */
  drawLogo(ctx, L - 14, y - 14, 1.5);
  ctx.font = `italic 700 42px ${FH}`;
  ctx.fillStyle = "#655d54";
  ctx.fillText("OurCuration", L + 68, y + 4);

  // QR code (right side)
  if (profileUrl) {
    try {
      const qrDataUrl = await QRCode.toDataURL(profileUrl, {
        width: QR_SIZE * 2, margin: 0,
        color: { dark: "#655d54", light: "#ffffff00" },
        errorCorrectionLevel: "M",
      });
      const qrImg = await loadImage(qrDataUrl);
      const qrX = L + INNER - QR_SIZE;
      const qrY = y - 10;
      // White background circle behind QR
      ctx.fillStyle = "#fff";
      roundRect(ctx, qrX - 6, qrY - 6, QR_SIZE + 12, QR_SIZE + 12, 10);
      ctx.fill();
      ctx.drawImage(qrImg, qrX, qrY, QR_SIZE, QR_SIZE);
      // "Scan me" label
      ctx.font = `600 18px ${FL}`;
      ctx.fillStyle = "#a8a29e";
      ctx.textAlign = "center";
      ctx.fillText("Scan to visit", qrX + QR_SIZE / 2, qrY + QR_SIZE + 10);
      ctx.textAlign = "left";
    } catch { /* ignore QR errors */ }
  }

  ctx.restore();

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), "image/png");
  });
}

function drawAvatarFallback(ctx, x, y, size, nickname, fh) {
  const grad = ctx.createLinearGradient(x, y, x + size, y + size);
  grad.addColorStop(0, "#595149");
  grad.addColorStop(1, "#655d54");
  ctx.beginPath();
  ctx.arc(x + size / 2, y + size / 2, size / 2, 0, Math.PI * 2);
  ctx.fillStyle = grad; ctx.fill();
  ctx.font = `italic 700 ${Math.round(size * 0.45)}px ${fh}`;
  ctx.fillStyle = "#fff6ef";
  ctx.textAlign = "center"; ctx.textBaseline = "middle";
  ctx.fillText((nickname || "?")[0].toUpperCase(), x + size / 2, y + size / 2);
  ctx.textAlign = "left"; ctx.textBaseline = "top";
}


export async function shareProfileCard(user, places, profileUrl) {
  const blob = await generateProfileCard(user, places, profileUrl);
  const file = new File([blob], `${user.nickname}-ourcuration-profile.png`, { type: "image/png" });

  // Share image + profile URL
  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: `${user.nickname}'s Space`, url: profileUrl });
      return "shared";
    } catch {
      return "cancelled";
    }
  }

  // Fallback: download image
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${user.nickname}-ourcuration-profile.png`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return "downloaded";
}

export async function shareCard(place, user) {
  const blob = await generateShareCard(place, user);
  const file = new File([blob], `${place.name}-ourcuration.png`, { type: "image/png" });

  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file], title: place.name });
      return "shared";
    } catch {
      return "cancelled";
    }
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${place.name}-ourcuration.png`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  return "downloaded";
}
